
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useRevealOnScroll } from '../hooks/useRevealOnScroll';
import { useScrollParallax } from '../hooks/useScrollParallax';
import { useProtocolMotion } from '../hooks/useProtocolMotion';
import {
  ShieldCheck, ArrowRight, Lock, X, Loader2, Handshake, MapPin, Mail,
  Check, FileText, Mic, Briefcase, FileWarning, Gavel, Scale,
  Globe, Send, Calendar, Linkedin, ChevronRight,
} from 'lucide-react';
import ChatWidget from './ChatWidget';
import {
  PRODUCT_NAME, PRODUCT_NAME_MARKETING,
  FEATURE_DIKTAT, FEATURE_LITIGATION,
} from '../constants';
import {
  T05_LEGAL_PAYMENT_PARAGRAPH,
  T05_LEGAL_STAND,
  T05_LEGAL_TRADEMARK_FOOTNOTE,
} from './products/t05/t05LegalContent';
import {
  BETRIEBSART_ARKORBIT_DESC,
  BETRIEBSART_ARKORBIT_TITLE,
  BETRIEBSART_KANZLEI_DESC,
  BETRIEBSART_KANZLEI_TITLE,
  ORBIS_ALL_IN_ONE_PLATFORM,
  ORBIS_HERO_LEAD,
} from './landing-templates/t05-launchdarkly/t05PublicCopy';

/** Typografisch: nbsp nach § — verhindert visuelles „§9“-Zusammenkleben */
const S9_RAO = '§\u00a09\u00a0RAO';

// ── Animated Counter ──────────────────────────────────────────────────────────
const AnimatedCounter: React.FC<{ target: number; suffix?: string; prefix?: string; duration?: number }> = ({
  target, suffix = '', prefix = '', duration = 1800
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started.current) {
        started.current = true;
        const startTime = Date.now();
        const tick = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.round(eased * target));
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);
  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

// ── ROI Calculator entfernt – Stub für späteren Wiedereinsatz ─────────────────
const _ROICalculatorStub = null;

// ── Scrolling Trust Badge Bar ──────────────────────────────────────────────────
const TrustBar: React.FC = () => {
  const badges = [
    `${S9_RAO} konform`, '100 % Self-Hosted', 'Kein OpenAI', 'DSGVO Art. 28',
    'Hetzner Frankfurt', 'E2EE verschlüsselt', 'Kein CLOUD Act', 'AVV unterzeichnet',
    '6 / 6 EU-Anforderungen', 'Zero-Retention', 'ISO 27001 Hosting', 'Made in Austria',
  ];
  return (
    <div className="trust-bar-protocol relative overflow-hidden border-y border-white/[0.08] py-4 bg-[#060a0f]">
      {/* Fade edges */}
      <div className="absolute left-0 top-0 w-16 h-full bg-gradient-to-r from-[#060a0f] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 w-16 h-full bg-gradient-to-l from-[#060a0f] to-transparent z-10 pointer-events-none" />
      <div className="flex animate-trust-scroll gap-12 whitespace-nowrap w-max" style={{ willChange: 'transform' }}>
        {[...badges, ...badges].map((b, i) => (
          <span key={i} className="text-slate-500 text-[10px] font-medium uppercase flex items-center gap-2.5">
            <span className="trust-bar-dot inline-block flex-shrink-0" />
            {b}
          </span>
        ))}
      </div>
    </div>
  );
};

interface LandingPageProps {
  onLoginClick: () => void;
  onLeadSubmit: (data: { name: string; email: string; company: string; interest: string; quelle?: string }) => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onLoginClick, onLeadSubmit }) => {
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [isImpressumOpen, setIsImpressumOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isAGBOpen, setIsAGBOpen] = useState(false);
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
  const [formStep, setFormStep] = useState<'form' | 'success'>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', company: '', interest: 'Analyse anfragen', quelle: '' });
  const [partnerData, setPartnerData] = useState({ name: '', email: '' });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const LEGAL_ENTITY = {
    NAME: "Andreas Berger, Inhaber ARKORBIT AI Austria",
    BRAND: "ARKORBIT AI", TYPE: "Einzelunternehmer", OWNER: "Andreas Berger",
    LOCATION: "3451 Michelhausen", ADDRESS: "Grillenbergsiedlung 1/5/1",
    FN: "Nicht im Firmenbuch eingetragen", UID: "ATU83006408"
  };

  const openAudit = () => { setFormStep('form'); setFormData(p => ({ ...p, interest: 'Analyse anfragen' })); setIsLeadModalOpen(true); };

  useEffect(() => {
    const handleScroll = () => setShowStickyCTA(window.scrollY > 600);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useRevealOnScroll();
  useScrollParallax();
  useProtocolMotion();

  // ── Parallax in-view class via IntersectionObserver ─────────────────────
  useEffect(() => {
    const noMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (noMotion) return;
    const sections = document.querySelectorAll('[data-parallax]');
    const obs = new IntersectionObserver(
      (entries) => entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add('in-view');
      }),
      { threshold: 0.08 }
    );
    sections.forEach(s => obs.observe(s));
    return () => obs.disconnect();
  }, []);
  // ────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#agb') setIsAGBOpen(true);
    if (hash === '#datenschutz') setIsPrivacyOpen(true);
    if (hash === '#impressum') setIsImpressumOpen(true);
    if (hash === '#audit') openAudit();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('openLead') === '1') {
      setFormData(p => ({ ...p, interest: decodeURIComponent(params.get('interest') || 'Allgemeine Anfrage') }));
      setFormStep('form'); setIsLeadModalOpen(true);
      window.history.replaceState({}, '', window.location.pathname + (window.location.hash || ''));
    }
    const src = params.get('utm_source') || '';
    const med = params.get('utm_medium') || '';
    const camp = params.get('utm_campaign') || '';
    if (src || med || camp) {
      const quelle = [src, med, camp].filter(Boolean).join(' / ');
      setFormData(p => ({ ...p, quelle }));
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault(); setIsSubmitting(true);
    setTimeout(() => {
      onLeadSubmit({ ...formData, quelle: formData.quelle || 'direkt' });
      setIsSubmitting(false); setFormStep('success');
    }, 1000);
  };

  const handlePartnerSubmit = (e: React.FormEvent) => {
    e.preventDefault(); setIsSubmitting(true);
    setTimeout(() => {
      onLeadSubmit({ name: partnerData.name, email: partnerData.email, company: 'Partner Bewerbung', interest: 'Affiliate Programm' });
      setIsSubmitting(false); setIsPartnerModalOpen(false); setPartnerData({ name: '', email: '' });
    }, 1000);
  };

  const complianceBadges = [
    { label: S9_RAO, sub: 'Anwaltsgeheimnis' },
    { label: 'DSGVO', sub: 'Art. 28 AVV' },
    { label: 'Self-Hosted', sub: 'EU · kein US-Cloud' },
    { label: 'Kein CLOUD Act', sub: 'Keine US-Cloud' },
    { label: 'E2EE', sub: 'Ende-zu-Ende' },
    { label: 'ISO 27001', sub: 'Hetzner Frankfurt' },
  ];

  return (
    <div className="bg-[#060a0f] text-slate-200 min-h-screen font-sans overflow-x-hidden selection:bg-blue-500/30 selection:text-white">

      {/* ══ GLOBALES RASTER – fixiert über gesamte Seite (Nothing.tech) ══ */}
      <div className="page-grid-global" aria-hidden="true" />

      {/* ── STICKY MOBILE CTA ── */}
      <div className={`fixed bottom-6 right-4 z-40 transition-all duration-500 ${showStickyCTA ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'}`}>
        <button onClick={openAudit}
          className="bg-blue-600 hover:bg-blue-500 text-white rounded-md px-5 py-3 text-sm font-semibold flex items-center gap-2 shadow-[0_8px_30px_rgba(37,99,235,0.4)] transition-all">
          <Calendar className="w-4 h-4" /> Analyse anfragen
        </button>
      </div>

      {/* ── NAVIGATION ── */}
      <nav className="fixed w-full z-50 top-0 left-0 right-0 bg-[#060a0f]/95 md:bg-[#060a0f]/90 backdrop-blur-md md:backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer group flex-shrink-0" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className="w-8 h-8 rounded-lg border border-white/10 overflow-hidden bg-slate-900 flex-shrink-0">
              <img src="/arkorbit-wappen.png" alt="ARKORBIT"
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 62%' }}
                decoding="async" width="32" height="32" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-white font-semibold tracking-wide text-sm">ARKORBIT</span>
              <span className="text-[9px] text-slate-500 tracking-widest font-medium uppercase">Anvertraut. Geschützt.</span>
            </div>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-7 text-[13px] text-slate-400 font-medium">
            <a href="#plattform" className="hover:text-white transition-colors">Plattform</a>
            <Link to="/produkt/orbis" className="hover:text-sky-400 transition-colors">Orbis</Link>
            <Link to="/produkt/litigation-engine" className="hover:text-violet-400 transition-colors">{FEATURE_LITIGATION}</Link>
            <a href="#investition" className="hover:text-white transition-colors">Angebot</a>
            <a href="#lexcore" className="hover:text-white transition-colors">LexCore</a>
            <a href="#sicherheit" className="hover:text-white transition-colors">Sicherheit</a>
            <button onClick={() => setIsPartnerModalOpen(true)} className="flex items-center gap-1.5 hover:text-white transition-colors">
              <Handshake className="w-3.5 h-3.5" /> Partner
            </button>
          </div>

          {/* Right CTAs */}
          <div className="flex items-center gap-3">
            <button onClick={onLoginClick}
              className="hidden sm:flex items-center gap-2 text-[13px] text-slate-400 hover:text-white transition-colors font-medium">
              <Lock className="w-3.5 h-3.5" /> Kunden-Login
            </button>
            <button onClick={openAudit}
              className="btn-protocol-primary bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 text-[13px] font-semibold transition-all hover:scale-[1.02] flex items-center gap-1.5">
              Demo anfragen <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section id="hero" className="relative pt-40 pb-32 px-6 overflow-hidden">

        {/* Statisches Hintergrund-Grid */}
        <div className="hero-bg-grid" aria-hidden="true" />

        {/* Background glows */}
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
          <div className="hidden md:block absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-blue-600/5 rounded-full blur-[150px]" />
          <div className="hidden lg:block absolute top-1/2 right-0 -translate-y-1/2 w-[480px] h-[480px] bg-blue-600/3 rounded-full blur-[100px]" />
        </div>

        {/* Hero Visual – orbit rings + shield, weit am Rand, kollidiert nie mit Text */}
        <div className="hidden xl:block absolute right-[-90px] top-1/2 -translate-y-[55%] w-[340px] h-[340px] pointer-events-none select-none opacity-60" style={{ zIndex: 1 }} aria-hidden="true">
          <div className="relative w-full h-full">
            {/* Rotating rings */}
            <div className="absolute inset-0 animate-orbit-slow opacity-[0.18]">
              <svg viewBox="0 0 360 360" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                <circle cx="180" cy="180" r="165" stroke="#2563eb" strokeWidth="1" strokeDasharray="10 14" />
                <circle cx="180" cy="180" r="124" stroke="#3b82f6" strokeWidth="0.7" strokeDasharray="6 10" />
                <circle cx="180" cy="180" r="84"  stroke="#60a5fa" strokeWidth="0.5" strokeDasharray="4 8" />
              </svg>
            </div>
            {/* Counter-rotating ring */}
            <div className="absolute inset-0 opacity-[0.08]" style={{ animation: 'orbit-slow 34s linear infinite reverse' }}>
              <svg viewBox="0 0 360 360" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                <circle cx="180" cy="180" r="145" stroke="#60a5fa" strokeWidth="0.7" strokeDasharray="3 15" />
              </svg>
            </div>
            {/* Shield center */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 rounded-lg bg-blue-500/8 border border-blue-500/20 flex items-center justify-center">
                <ShieldCheck className="w-10 h-10 text-blue-400 opacity-60" />
              </div>
            </div>
            {/* Trust tags – Mono, blau, sharp; bleiben innerhalb des Rings */}
            <div className="absolute font-mono text-[10px] font-medium tracking-[0.1em] px-2.5 py-1 rounded-[2px] border"
              style={{ top: '12%', left: '34%', color: '#60a5fa', borderColor: 'rgba(96,165,250,0.25)', background: 'rgba(37,99,235,0.07)' }}>{S9_RAO}</div>
            <div className="absolute font-mono text-[10px] font-medium tracking-[0.1em] px-2.5 py-1 rounded-[2px] border"
              style={{ top: '76%', left: '30%', color: '#60a5fa', borderColor: 'rgba(96,165,250,0.25)', background: 'rgba(37,99,235,0.07)' }}>EU SERVER</div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto relative z-10">
          {/* Protocol kicker (zkPass-style, ARKORBIT blue) */}
          <div className="flex flex-col items-center gap-3 mb-8" data-hero-seq>
            <span className="protocol-kicker">
              <span className="protocol-kicker-dot" aria-hidden="true" />
              Legal-Tech Protocol · EU Self-Hosted
            </span>
            <span className="inline-flex items-center gap-2 text-[11px] text-slate-500 font-medium tracking-wide">
              <img src="https://flagcdn.com/w20/at.png" alt="AT" className="w-3.5 h-auto rounded-sm opacity-90" />
              Österreichs erste Self-Hosted Kanzlei-KI
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-center text-5xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-[-0.03em] leading-[1.05] mb-6" data-hero-seq>
            Wir schützen,<br />
            <span className="text-blue-400">was Ihnen anvertraut wurde.</span>
          </h1>

          {/* Sub – sagt in einem Satz, was ARKORBIT ist und löst */}
          <p className="text-center text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed font-light" data-hero-seq>
            <span className="text-slate-200 font-normal">{PRODUCT_NAME_MARKETING}</span> ist die{' '}
            {ORBIS_ALL_IN_ONE_PLATFORM} für Kanzleien.{' '}
            {ORBIS_HERO_LEAD}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-hero-seq>
            <button onClick={openAudit}
              className="btn-protocol-primary w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 font-semibold text-base transition-all hover:scale-[1.02] flex items-center justify-center gap-2">
              Analyse anfragen <ArrowRight className="w-4 h-4" />
            </button>
            <a href="#plattform"
              className="btn-protocol-outline w-full sm:w-auto text-slate-400 hover:text-white px-8 py-4 font-medium text-base transition-all flex items-center justify-center gap-2">
              Plattform entdecken <ChevronRight className="w-4 h-4" />
            </a>
          </div>

          {/* Stats — sharp protocol grid (zkPass-inspired) */}
          <div className="protocol-stat-grid mt-16" data-hero-seq>
            {[
              { val: '95', suffix: '%', label: 'Zeitersparnis Dokumentation' },
              { val: '0', suffix: '%', label: 'Daten verlassen Server' },
              { val: '6', suffix: '/6', label: 'EU-Kanzleianforderungen' },
            ].map((s, i) => (
              <div key={i} className="protocol-stat-cell">
                <div className="text-2xl md:text-3xl font-bold text-white tabular-nums tracking-tight">
                  <AnimatedCounter target={+s.val} suffix={s.suffix} duration={1600} />
                </div>
                <div className="protocol-stat-label">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Scroll invite */}
          <div className="flex justify-center mt-14" aria-hidden="true">
            <div className="w-px h-14 bg-gradient-to-b from-white/15 to-transparent animate-bounce-down" />
          </div>
        </div>
      </section>

      {/* ── TRUST BAR ── */}
      <TrustBar />

      {/* ── WARUM ORBIS: Fragmentierung → integrierte Plattform ── */}
      <section id="warum-orbis" data-parallax className="py-20 px-6 bg-[#060a0f] border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12" data-motion="rise">
            <span className="protocol-kicker mb-6">
              <span className="protocol-kicker-dot" aria-hidden="true" />
              Warum wechseln
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-6 mb-4 leading-tight">
              Warum Orbis — und nicht noch ein Tool
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Einzelne Lösungen decken Teilaufgaben ab. Kanzleiarbeit braucht einen durchgängigen Stack — auf Ihrem Server, unter Ihrer Kontrolle.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-white/[0.08] border border-white/[0.08] rounded-md overflow-hidden" data-motion="rise">
            {[
              {
                title: 'Fragmentierung',
                text: 'Diktat-Software, Aktenverwaltung, US-Cloud-KI, Messenger und Recherche-Tools liefern jeweils einen Teil — ohne gemeinsamen Datenfluss. Jeder Medienbruch kostet Zeit und erhöht das Compliance-Risiko.',
              },
              {
                title: 'Integrierte Plattform',
                text: 'Orbis verbindet Diktat, PDF-Erstellung, Akte, ARKO und LEXCORE in einem Stack auf Ihrem dedizierten EU-Server. Ein Login, ein Workflow, quellenbelegte Rechtsrecherche statt generischer KI-Antworten.',
              },
              {
                title: 'Messbarer Nutzen',
                text: 'Weniger manuelle Zwischenschritte, nachvollziehbare Dokumentation, Compliance durch Architektur statt Einzellösungen. Der Wechsel ist kein Patch — sondern ein Upgrade auf integrierte Kanzlei-Infrastruktur.',
              },
            ].map((block) => (
              <div key={block.title} className="bg-[#060a0f] p-7 md:p-8">
                <h3 className="text-white font-semibold text-base mb-3">{block.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{block.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROBLEM → LÖSUNG: eine Gegenüberstellung, sofort erfassbar ── */}
      <section id="problem" data-parallax className="py-24 px-6 bg-[#04070b] border-y border-white/5 overflow-hidden">
        <div className="max-w-6xl mx-auto">

          <div className="text-center mb-14" data-motion="rise">
            <span className="protocol-kicker mb-6">
              <span className="protocol-kicker-dot" aria-hidden="true" />
              Problem → Lösung
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-6 mb-5 leading-tight">
              Einzelne Tools funktionieren — Ihre Kanzlei braucht einen Stack.<br />
              <span className="text-blue-400">Orbis ist die integrierte Vollplattform.</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed text-lg">
              Diktat-Software, Akte, US-Cloud-KI und Messenger arbeiten isoliert. Zusammen entstehen Medienbrüche,
              Compliance-Risiken und Zeitverlust — zusätzlich zu {S9_RAO}- und DSGVO-Risiken durch US-Cloud und
              Standard-SaaS. Orbis vereint alles auf einem EU-Server: kein Patch, sondern Upgrade auf integrierte
              Kanzlei-Infrastruktur.
            </p>
          </div>

          {/* Gegenüberstellung – scharfes 1px-Grid */}
          <div className="grid md:grid-cols-2 gap-px bg-white/[0.08] border border-white/[0.08] rounded-md overflow-hidden mb-10">

            {/* Links: Heute */}
            <div className="bg-[#04070b] p-7 md:p-9" data-motion="slide-left">
              <div className="font-mono text-[11px] font-medium tracking-[0.12em] uppercase text-red-400/90 mb-7 flex items-center gap-2">
                <X className="w-3.5 h-3.5" /> Heute · US-Cloud &amp; Standard-SaaS
              </div>
              <div className="flex flex-col gap-6">
                {[
                  { label: S9_RAO,       title: 'Anwaltsgeheimnis verletzt',   text: 'Mandantenkommunikation über US-Server – bis zum Entzug der Zulassung.' },
                  { label: 'CLOUD ACT',     title: 'US-Behördenzugriff',          text: 'Meta, Google und Apple müssen US-Behörden jederzeit Zugriff gewähren – ohne Ihre Kenntnis.' },
                  { label: 'ART. 83 DSGVO', title: 'Bis zu 20 Mio. € Strafe',     text: 'Bei Verstößen mit sensiblen Mandantendaten – oder 4 % des Jahresumsatzes.' },
                ].map((c, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <X className="w-4 h-4 text-red-400/70 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.12em] text-slate-500 mb-1">{c.label}</div>
                      <h3 className="text-white font-semibold text-sm mb-1">{c.title}</h3>
                      <p className="text-slate-500 text-sm leading-relaxed">{c.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Rechts: Mit ARKORBIT */}
            <div className="bg-[#060a0f] p-7 md:p-9" data-motion="slide-right">
              <div className="font-mono text-[11px] font-medium tracking-[0.12em] uppercase text-blue-400 mb-7 flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" /> Mit ARKORBIT · Self-Hosted EU
              </div>
              <div className="flex flex-col gap-6">
                {[
                  { label: 'SELF-HOSTED',   title: 'Daten bleiben in der EU-Infrastruktur', text: '100 % Self-Hosted in der EU (Hetzner Frankfurt). Kein Drittanbieter, kein US-Zugriff – technisch unmöglich.' },
                  { label: 'ARCHITEKTUR',   title: `${S9_RAO} technisch implementiert`, text: 'Anwaltsgeheimnis durch Systemarchitektur garantiert, nicht durch Vertragsversprechen.' },
                  { label: 'E2EE · ART. 28', title: 'Verschlüsselt & vertraglich sauber', text: 'Ende-zu-Ende-Verschlüsselung plus vollständiger AVV nach Art. 28 DSGVO.' },
                ].map((c, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.12em] text-blue-400/70 mb-1">{c.label}</div>
                      <h3 className="text-white font-semibold text-sm mb-1">{c.title}</h3>
                      <p className="text-slate-400 text-sm leading-relaxed">{c.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Kennzahlen – Schmerz → Risiko → Alleinstellung */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.08] border border-white/[0.08] rounded-md overflow-hidden mb-12" data-motion="rise">
            {[
              { val: 3,  suffix: 'h',       prefix: '',     isHash: false, label: 'TÄGLICH PRO ANWALT',     sub: 'durch manuelle Dokumentation verloren' },
              { val: 20, suffix: ' Mio. €', prefix: 'bis ', isHash: false, label: 'STRAFE MÖGLICH',         sub: 'DSGVO-Verstoß bei sensiblen Mandantendaten' },
              { val: 1,  suffix: '',        prefix: '',     isHash: true,  label: 'IN ÖSTERREICH',          sub: 'die erste Self-Hosted Kanzlei-KI – das sind wir' },
            ].map((s, i) => (
              <div key={i} className="bg-[#060a0f] px-8 py-10 text-center">
                <div className="text-4xl md:text-5xl font-bold mb-3 tabular-nums text-white">
                  {s.isHash ? '#1' : <AnimatedCounter target={s.val} suffix={s.suffix} prefix={s.prefix} duration={1600} />}
                </div>
                <div className="font-mono text-[10px] tracking-[0.1em] text-blue-400/80 mb-1">{s.label}</div>
                <div className="text-slate-500 text-sm leading-relaxed">{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Ein CTA – blau, kein Farbwechsel */}
          <div className="text-center" data-motion="rise">
            <button onClick={openAudit}
              className="btn-protocol-primary inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 font-semibold transition-all hover:scale-[1.02]">
              Analyse anfragen <ArrowRight className="w-4 h-4" />
            </button>
            <p className="font-mono text-[10px] tracking-[0.12em] uppercase text-slate-600 mt-5">30 Minuten · Unverbindlich · EU Self-Hosted</p>
          </div>

        </div>
      </section>

      {/* ── PLATFORM SECTION ── */}
      <section id="plattform" data-parallax className="py-24 px-6">
        {/* Parallax-Hintergrund: ein blauer Glow, sonst Ruhe */}
        <div data-parallax-bg aria-hidden="true">
          <div className="absolute inset-0 bg-[#060a0f]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-blue-600/5 rounded-full blur-[140px]" />
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16" data-motion="rise">
            <span className="protocol-kicker">
              <span className="protocol-kicker-dot" aria-hidden="true" />
              Die Plattform
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-6 mb-4">Eine {ORBIS_ALL_IN_ONE_PLATFORM}.</h2>
            <p className="text-slate-400 text-lg max-w-xl mx-auto">
              {PRODUCT_NAME_MARKETING} — {ORBIS_HERO_LEAD}{' '}
              {BETRIEBSART_ARKORBIT_TITLE}: {BETRIEBSART_ARKORBIT_DESC}{' '}
              {BETRIEBSART_KANZLEI_TITLE}: {BETRIEBSART_KANZLEI_DESC}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 stagger-children">
            <Link to="/produkt/orbis"
              className="group bg-[#0d1117] border border-white/6 hover:border-blue-500/30 rounded-lg p-8 flex flex-col transition-all duration-300 hover:bg-blue-500/3">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-md bg-blue-500/10 border border-blue-500/15 flex items-center justify-center flex-shrink-0">
                  <Mic className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <div className="font-mono text-[10px] text-blue-400 font-medium uppercase tracking-[0.12em]">{ORBIS_ALL_IN_ONE_PLATFORM}</div>
                  <div className="text-white font-bold text-lg">Orbis</div>
                </div>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed flex-1 mb-6">
                Portal, Diktat &amp; Transkription, ARKO und BRAIN/LEXCORE — individuell nach Kanzleigröße.
                {' '}{BETRIEBSART_ARKORBIT_DESC}
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {['Kanzlei-Portal', `${FEATURE_DIKTAT}`, 'ARKO', 'OnlyOffice', 'BRAIN / LEXCORE'].map(t => (
                  <span key={t} className="font-mono text-[10px] tracking-[0.06em] px-2.5 py-1 bg-white/4 border border-white/8 text-slate-400 rounded-[2px]">{t}</span>
                ))}
              </div>
              <span className="inline-flex items-center gap-2 text-blue-400 text-sm font-semibold group-hover:text-blue-300 transition-colors">
                Zur Produktseite <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>

            <Link to="/produkt/litigation-engine"
              className="group bg-[#0d1a2e] border border-blue-500/20 hover:border-blue-500/40 rounded-lg p-8 flex flex-col transition-all duration-300 hover:bg-blue-500/5">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-md bg-blue-500/10 border border-blue-500/25 flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <div className="font-mono text-[10px] text-blue-400 font-medium uppercase tracking-[0.12em]">Modul nach Angebot</div>
                  <div className="text-white font-bold text-lg">{FEATURE_LITIGATION}</div>
                </div>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed flex-1 mb-6">
                Strategische Schriftsatz-Analyse mit doppelter Anonymisierung — Anthropic sieht keine echten Kanzleidaten.
                Umfang im Gespräch, kein öffentlicher Listenpreis.
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {['Modul von Orbis', `${FEATURE_LITIGATION}`, 'Doppelte Anonymisierung', '100 % DSGVO'].map(t => (
                  <span key={t} className="font-mono text-[10px] tracking-[0.06em] px-2.5 py-1 bg-white/4 border border-white/8 text-slate-400 rounded-[2px]">{t}</span>
                ))}
              </div>
              <span className="inline-flex items-center gap-2 text-blue-400 text-sm font-semibold group-hover:text-blue-300 transition-colors">
                Zur Produktseite <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>

          {/* Lego principle – compact */}
          <div className="mt-6 bg-[#0d1117] border border-white/6 rounded-lg p-6 reveal">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <p className="flex-1 text-slate-400 text-sm leading-relaxed">
                <strong className="text-white">Ihre Kanzlei. Ihre Regeln.</strong>{' '}
                {BETRIEBSART_ARKORBIT_TITLE}: {BETRIEBSART_ARKORBIT_DESC}{' '}
                {BETRIEBSART_KANZLEI_TITLE}: {BETRIEBSART_KANZLEI_DESC}
              </p>
              <button onClick={openAudit}
                className="flex-shrink-0 border border-blue-500/30 text-blue-400 hover:bg-blue-500/8 px-5 py-2.5 rounded-md text-sm font-semibold transition-all flex items-center gap-2 whitespace-nowrap">
                Kostenlose Analyse <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── ANGEBOT – kein öffentlicher Listenpreis ── */}
      <section id="investition" className="py-24 px-6 bg-[#04070b] border-y border-white/5 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14" data-motion="rise">
            <span className="protocol-kicker">
              <span className="protocol-kicker-dot" aria-hidden="true" />
              Angebot nach Kanzleigröße
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-6 mb-5 leading-tight">
              Individuelles Angebot<br />
              <span className="text-blue-400">statt Listenpreis.</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed text-lg">
              {PRODUCT_NAME_MARKETING} ist die {ORBIS_ALL_IN_ONE_PLATFORM}. Umfang, Sitze und Module legen wir im Gespräch fest.
              {BETRIEBSART_ARKORBIT_TITLE} oder {BETRIEBSART_KANZLEI_TITLE} — ohne öffentlichen Listenpreis.
            </p>
          </div>

          <div className="bg-[#0d1117] border border-white/6 rounded-lg p-6 md:p-8" data-motion="rise">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="max-w-2xl">
                <p className="text-slate-300 text-sm leading-relaxed mb-3">
                  <strong className="text-white">Individuelle Kanzlei-Lösung</strong> — kein öffentlicher Listenpreis.
                  Umfang, Infrastruktur und Kombination werden im persönlichen Gespräch festgelegt.
                </p>
                <p className="font-mono text-[10px] tracking-[0.1em] uppercase text-slate-600">
                  Kein Self-Service-Checkout · Verbindliches Angebot nach Demo &amp; Analyse
                </p>
              </div>
              <button onClick={openAudit}
                className="flex-shrink-0 btn-protocol-primary bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 font-semibold transition-all hover:scale-[1.02] flex items-center justify-center gap-2 whitespace-nowrap">
                Demo anfragen <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── LEXCORE – das Fundament beider Produkte ── */}
      <section id="lexcore" className="py-24 px-6 bg-[#04070b] border-y border-white/5 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">

            {/* Links: Was LEXCORE ist */}
            <div data-motion="slide-left">
              <span className="protocol-kicker">
                <span className="protocol-kicker-dot" aria-hidden="true" />
                LEXCORE · Norm-Engine
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-6 mb-5 leading-tight">
                Keine erfundenen Paragrafen.<br />
                <span className="text-blue-400">Jede Norm belegt.</span>
              </h2>
              <p className="text-slate-400 leading-relaxed mb-8">
                Beide Produkte arbeiten auf <span className="text-slate-200">LEXCORE</span> – unserer
                deterministischen Rechts-Engine mit kuratierter Normdatenbank aus RIS und OGH-Judikatur.
                Was die KI nicht belegen kann, wird verworfen, bevor Sie es sehen.
              </p>
              <div className="flex flex-col gap-5">
                {[
                  { label: 'KURATIERT',      title: 'RIS & OGH-Judikatur',          text: 'Fachlich kuratierter Normbestand statt blind gecrawlter Texte – laufend erweitert.' },
                  { label: 'DETERMINISTISCH', title: 'Norm-Anker statt Ratespiel',   text: 'Paragraf rein, geprüfte Fundstelle raus. Reproduzierbar, nachvollziehbar, zitierfähig.' },
                  { label: 'QUELLENPFLICHT', title: 'Anti-Halluzinations-Gate',     text: 'Jede zitierte Norm muss in den geprüften Quellen der Antwort liegen – sonst wird sie verworfen.' },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.12em] text-blue-400/70 mb-1">{f.label}</div>
                      <h3 className="text-white font-semibold text-sm mb-1">{f.title}</h3>
                      <p className="text-slate-500 text-sm leading-relaxed">{f.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Rechts: Protokoll-Visual – so arbeitet LEXCORE */}
            <div data-motion="slide-right">
              <div className="bg-[#060a0f] border border-white/[0.08] rounded-md overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06]">
                  <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-slate-500">lexcore · prüfprotokoll</span>
                  <span className="protocol-kicker-dot" aria-hidden="true" />
                </div>
                <div className="p-5 font-mono text-[12px] leading-[2.1] text-slate-400">
                  <div><span className="text-slate-600">&gt;</span> anfrage: <span className="text-slate-200">befristung mietvertrag § 29 MRG</span></div>
                  <div><span className="text-emerald-400">✓</span> norm gefunden: <span className="text-blue-400">[MRG-29]</span> <span className="text-slate-600">· RIS, kuratiert</span></div>
                  <div><span className="text-emerald-400">✓</span> judikatur: <span className="text-slate-200">passende OGH-Entscheidungen</span> <span className="text-slate-600">· verknüpft</span></div>
                  <div><span className="text-red-400">✗</span> <span className="text-slate-500 line-through">[MRG-5]</span> <span className="text-slate-600">· nicht im geprüften Korpus → verworfen</span></div>
                  <div className="mt-2 pt-3 border-t border-white/[0.06]">
                    <span className="text-emerald-400">✓</span> antwort freigegeben <span className="text-slate-600">· nur belegte quellen</span>
                  </div>
                </div>
              </div>
              <p className="font-mono text-[10px] tracking-[0.1em] uppercase text-slate-600 mt-4 text-center">
                Zentral betrieben · ohne Mandanten-Daten · EU
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ── SECURITY SECTION ── */}
      <section id="sicherheit" data-parallax className="py-24 px-6">
        {/* Parallax-Hintergrund: ruhig, ein blauer Glow */}
        <div data-parallax-bg aria-hidden="true">
          <div className="absolute inset-0 bg-[#060a0f]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-blue-600/4 rounded-full blur-[140px]" />
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16" data-motion="rise">
            <span className="protocol-kicker">
              <span className="protocol-kicker-dot" aria-hidden="true" />
              Sicherheit &amp; Compliance
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-6 mb-4">Enterprise-Sicherheit. EU-Niveau.</h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Wir sind der einzige bekannte Anbieter in der EU, der alle 6 Anforderungen österreichischer Kanzleien gleichzeitig erfüllt.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/[0.08] border border-white/[0.08] rounded-md overflow-hidden mb-12" data-motion="rise">
            {complianceBadges.map((b, i) => (
              <div key={i} className="bg-[#060a0f] px-5 py-4 flex items-center gap-3.5">
                <ShieldCheck className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <div>
                  <div className="text-white font-semibold text-sm">{b.label}</div>
                  <div className="font-mono text-[10px] tracking-[0.08em] uppercase text-slate-500">{b.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ── EU-Anforderungen: 3 kompakte Kartengruppen (mobil-freundlich) ── */}
          <div className="grid md:grid-cols-3 gap-px bg-white/[0.08] border border-white/[0.08] rounded-md overflow-hidden stagger-children">

            {/* Karte 1: Infrastruktur */}
            <div className="bg-[#0d1117] p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-md bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0">
                  <X className="w-4 h-4 text-red-400" />
                </div>
                <div>
                  <p className="font-mono text-[10px] font-medium text-red-400/80 uppercase tracking-[0.12em]">US-Cloud & gängige SaaS</p>
                  <p className="text-white font-semibold text-sm">Infrastruktur & Souveränität</p>
                </div>
              </div>
              <ul className="space-y-3">
                {[
                  'Daten liegen auf Fremdservern',
                  'Kein Self-Hosting möglich',
                  'US-CLOUD Act: behördlicher Zugriff möglich',
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-slate-500 text-sm">
                    <X className="w-3.5 h-3.5 text-red-500/50 flex-shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
            </div>

            {/* Karte 2: Recht & Kommunikation */}
            <div className="bg-[#0d1117] p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-md bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0">
                  <X className="w-4 h-4 text-red-400" />
                </div>
                <div>
                  <p className="font-mono text-[10px] font-medium text-red-400/80 uppercase tracking-[0.12em]">US-Cloud & gängige SaaS</p>
                  <p className="text-white font-semibold text-sm">Recht & Verschlüsselung</p>
                </div>
              </div>
              <ul className="space-y-3">
                {[
                  `${S9_RAO} technisch nicht garantierbar`,
                  'Keine Ende-zu-Ende-Verschlüsselung',
                  'AVV nach Art. 28 DSGVO oft unvollständig',
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-slate-500 text-sm">
                    <X className="w-3.5 h-3.5 text-red-500/50 flex-shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
            </div>

            {/* Karte 3: ARKORBIT */}
            <div className="bg-[#0d1a2e] p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-md bg-blue-500/10 border border-blue-500/25 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <p className="font-mono text-[10px] font-medium text-blue-400 uppercase tracking-[0.12em]">ARKORBIT · Self-Hosted</p>
                  <p className="text-white font-semibold text-sm">Alle 6 Anforderungen</p>
                </div>
              </div>
              <ul className="space-y-3">
                {[
                  '100 % Self-Hosted · Daten bleiben in der EU',
                  `${S9_RAO} technisch implementiert`,
                  'E2EE + AVV Art. 28 DSGVO vollständig',
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-slate-300 text-sm">
                    <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
              <div className="mt-5 pt-5 border-t border-blue-500/15 flex items-center justify-between">
                <span className="font-mono text-[10px] text-blue-400 font-medium uppercase tracking-[0.12em]">Gesamterfüllung</span>
                <span className="text-white font-bold text-lg">6 / 6</span>
              </div>
            </div>

          </div>
          {/* Vergleichsübersicht – vollständig responsive, kein horizontales Scrollen */}
          <details className="mt-6">
            <summary className="cursor-pointer text-xs text-slate-600 hover:text-slate-400 transition-colors text-center py-2 select-none">
              Vollständige Vergleichsübersicht anzeigen
            </summary>
            <div className="mt-4 bg-[#0d1117] border border-white/6 rounded-md overflow-hidden">
              {/* Header – Mobile: 3 Spalten (Anforderung, Mitbewerber, ARKORBIT); Desktop: 4 Spalten */}
              <div className="border-b border-white/6 px-4 md:px-6 py-3">
                <div className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[1fr_auto_auto_auto] gap-x-3 text-[10px] md:text-xs font-semibold uppercase tracking-widest text-slate-500">
                  <span>Anforderung</span>
                  <span className="text-center w-14 md:w-20 text-red-400/70">Mitbew.</span>
                  <span className="hidden md:block text-center w-20 text-slate-500">EU SaaS</span>
                  <span className="text-center w-14 md:w-20 text-blue-400">ARKORBIT</span>
                </div>
              </div>
              {[
                { label: '100 % Self-Hosted – kein Drittanbieter', us: false, eu: false },
                { label: 'Daten in Österreich / EU', us: false, eu: true },
                { label: 'Kein OpenAI · Kein US-CLOUD Act', us: false, eu: false },
                { label: `${S9_RAO} – technisch garantiert`, us: false, eu: false },
                { label: 'E2E-verschlüsselte Kommunikation', us: false, eu: false },
                { label: 'AVV nach Art. 28 DSGVO', us: false, eu: true },
              ].map((row, i) => (
                <div key={i} className={`grid grid-cols-[1fr_auto_auto] md:grid-cols-[1fr_auto_auto_auto] gap-x-3 items-center px-4 md:px-6 py-3 text-sm ${i % 2 === 0 ? 'bg-white/[0.01]' : ''} border-b border-white/4 last:border-0`}>
                  <span className="text-slate-400 text-xs md:text-sm leading-snug">{row.label}</span>
                  <span className="flex justify-center w-14 md:w-20">
                    <X className="w-4 h-4 text-red-500/60" />
                  </span>
                  <span className="hidden md:flex justify-center w-20">
                    {row.eu ? <Check className="w-4 h-4 text-emerald-500/60" /> : <X className="w-4 h-4 text-red-500/60" />}
                  </span>
                  <span className="flex justify-center w-14 md:w-20">
                    <Check className="w-4 h-4 text-emerald-400" />
                  </span>
                </div>
              ))}
              <div className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[1fr_auto_auto_auto] gap-x-3 items-center px-4 md:px-6 py-4 bg-blue-500/5 border-t border-blue-500/10 font-bold text-sm">
                <span className="text-white">Gesamterfüllung</span>
                <span className="text-center w-14 md:w-20 text-red-400">0 / 6</span>
                <span className="hidden md:block text-center w-20 text-slate-400">2 / 6</span>
                <span className="text-center w-14 md:w-20 text-blue-400">6 / 6</span>
              </div>
            </div>
          </details>
        </div>
      </section>

      {/* ── IDENTITÄT – Etymologie (stille Markenbotschaft vor Footer) ── */}
      <div className="py-16 px-6 text-center bg-[#060a0f]">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] text-slate-700 font-mono tracking-[0.2em] select-none">
            lat.&nbsp;
            <span className="text-slate-500">Arca</span>
            <span className="text-slate-700 mx-1.5">·</span>
            <span className="text-slate-600 text-[10px]">der sichere Behälter</span>
            <span className="text-slate-700 mx-2">+</span>
            <span className="text-slate-500">Orbita</span>
            <span className="text-slate-700 mx-1.5">·</span>
            <span className="text-slate-600 text-[10px]">der festgelegte Kreislauf</span>
            <span className="text-slate-700 mx-2">=</span>
            <span className="text-slate-400 font-semibold tracking-[0.15em]">[ark-orbit]</span>
            <span className="text-slate-700 mx-2">—</span>
            <span className="text-slate-500 italic">Der geschützte Kreislauf</span>
          </p>
        </div>
      </div>

      {/* ── FINAL CTA SECTION ── */}
      <section id="audit" data-parallax className="py-24 px-6">
        {/* Parallax-Hintergrund: blauer Bloom von unten – Peak-Erlebnis */}
        <div data-parallax-bg aria-hidden="true">
          <div className="absolute inset-0 bg-[#060a0f]" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-blue-600/8 rounded-full blur-[140px]" />
        </div>

        <div className="max-w-4xl mx-auto text-center" data-motion="rise">
          <div className="bg-[#0d1117] border border-white/8 rounded-lg p-12 md:p-16 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
            <div className="relative z-10">
              <span className="protocol-kicker mb-6">
                <span className="protocol-kicker-dot" aria-hidden="true" />
                Bereit loszulegen
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-white mt-6 mb-4">
                Für Kanzleien, die<br />keine Kompromisse machen.
              </h2>
              <p className="text-slate-400 text-lg max-w-xl mx-auto mb-8 leading-relaxed">
                Wir analysieren Ihre Infrastruktur und zeigen Ihnen in 30 Minuten,
                wo ARKORBIT sofort greift. Unverbindlich. Keine Standard-Pakete. Nur das, was Ihre Kanzlei wirklich braucht.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button onClick={openAudit}
                  className="btn-protocol-primary w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white px-8 py-4 font-semibold transition-all hover:scale-[1.02] flex items-center justify-center gap-2">
                  Kostenlose Analyse anfragen <ArrowRight className="w-4 h-4" />
                </button>
                <a href="mailto:office@arkorbit.at"
                  className="btn-protocol-outline w-full sm:w-auto text-slate-400 hover:text-white px-8 py-4 font-medium transition-all flex items-center justify-center gap-2">
                  <Mail className="w-4 h-4" /> office@arkorbit.at
                </a>
              </div>
              {/* Direkte Stripe-Zahlungslinks entfernt – Buchung nur nach persönlicher Kontaktaufnahme */}
              <p className="font-mono text-[10px] tracking-[0.12em] uppercase text-slate-600 mt-6">30 Minuten · Unverbindlich · Kein Standard-Paket</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── RECHTLICHES ── */}
      <section className="py-20 px-6 bg-[#060a0f] border-t border-white/5">
        <div className="max-w-5xl mx-auto">

          {/* Heading – groß und klar */}
          <div className="mb-12 reveal">
            <p className="text-slate-600 text-xs font-semibold uppercase tracking-widest mb-3">Transparenz</p>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                  Rechtliches.
                </h2>
                <p className="text-slate-400 mt-3 text-base max-w-xl leading-relaxed">
                  Wir sind stolz darauf, diese Dokumente herzuzeigen. DSGVO, AVV und AGB sind kein Kleingedrucktes – sie sind Teil unseres Versprechens.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="protocol-kicker-dot" aria-hidden="true" />
                <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-blue-400">Vollständig · Österreichisches Recht</span>
              </div>
            </div>
            <div className="w-full h-px bg-gradient-to-r from-white/8 via-white/3 to-transparent mt-8" />
          </div>

          {/* 3 Karten */}
          <div className="grid md:grid-cols-3 gap-5 stagger-children">

            {/* AGB & AVV */}
            <button onClick={() => setIsAGBOpen(true)}
              className="group bg-[#0d1117] border border-white/6 hover:border-blue-500/30 hover:bg-blue-500/3 rounded-lg p-7 text-left transition-all duration-300 w-full">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-md bg-blue-500/10 border border-blue-500/15 flex items-center justify-center flex-shrink-0">
                  <Gavel className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <div className="font-mono text-blue-400 text-[10px] font-medium uppercase tracking-[0.12em]">§ 1–8</div>
                  <div className="text-white font-bold text-sm">AGB & AVV</div>
                </div>
              </div>
              <p className="text-slate-500 text-sm leading-relaxed mb-5">
                Leistungsumfang, Haftung, SLA 98 %, Kündigung, Gerichtsstand Österreich. Inkl. Auftragsverarbeitungsvertrag (AVV) nach Art. 28 DSGVO.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-blue-400 text-xs font-semibold group-hover:text-blue-300 transition-colors">Dokument öffnen</span>
                <ChevronRight className="w-4 h-4 text-blue-400/50 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all" />
              </div>
            </button>

            {/* Datenschutz */}
            <button onClick={() => setIsPrivacyOpen(true)}
              className="group bg-[#0d1117] border border-white/6 hover:border-blue-500/30 hover:bg-blue-500/3 rounded-lg p-7 text-left transition-all duration-300 w-full">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-md bg-blue-500/10 border border-blue-500/15 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <div className="font-mono text-blue-400 text-[10px] font-medium uppercase tracking-[0.12em]">DSGVO</div>
                  <div className="text-white font-bold text-sm">Datenschutz</div>
                </div>
              </div>
              <p className="text-slate-500 text-sm leading-relaxed mb-5">
                Welche Daten wir verarbeiten, unsere Sub-Auftragsverarbeiter (Hetzner, Stripe, Self-Hosted Matrix) und Ihre Rechte nach Art. 15–21 DSGVO.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-blue-400 text-xs font-semibold group-hover:text-blue-300 transition-colors">Dokument öffnen</span>
                <ChevronRight className="w-4 h-4 text-blue-400/50 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all" />
              </div>
            </button>

            {/* Impressum */}
            <button onClick={() => setIsImpressumOpen(true)}
              className="group bg-[#0d1117] border border-white/6 hover:border-slate-500/30 hover:bg-white/2 rounded-lg p-7 text-left transition-all duration-300 w-full">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-md bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                  <Scale className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <div className="font-mono text-slate-500 text-[10px] font-medium uppercase tracking-[0.12em]">§ 25 MedG</div>
                  <div className="text-white font-bold text-sm">Impressum</div>
                </div>
              </div>
              <p className="text-slate-500 text-sm leading-relaxed mb-5">
                Medieninhaber, Unternehmensgegenstand, WKO NÖ (UBIT), Aufsicht BH Tulln, UID ATU83006408. Vollständig gemäß § 25 Mediengesetz & § 5 ECG.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-xs font-semibold group-hover:text-white transition-colors">Dokument öffnen</span>
                <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400 group-hover:translate-x-0.5 transition-all" />
              </div>
            </button>

          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#060a0f] border-t border-white/5 pt-16 pb-10 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 rounded-lg border border-white/10 overflow-hidden bg-slate-900 flex-shrink-0 flex items-center justify-center">
                  <img src="/arkorbit-wappen.png" alt="ARKORBIT"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: 'center 62%' }}
                    width="32" height="32" />
                </div>
                <span className="text-white font-semibold tracking-wide">ARKORBIT AI Austria</span>
              </div>
              <p className="text-slate-500 text-sm leading-relaxed max-w-xs mb-5">
                Wir verbinden juristische Exzellenz mit technologischer Souveränität. Für Kanzleien, die keine Kompromisse machen.
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  { icon: <ShieldCheck className="w-3 h-3 mr-1" />, label: '100% Self-Hosted', cls: 'text-emerald-500 border-emerald-500/20 bg-emerald-950/20' },
                  { icon: <FileText className="w-3 h-3 mr-1" />, label: 'AVV Hetzner ✓', cls: 'text-blue-400 border-blue-500/20 bg-blue-950/20' },
                  { icon: null, label: 'UID ATU83006408', cls: 'text-slate-500 border-slate-700 bg-slate-900/50' },
                ].map((b, i) => (
                  <span key={i} className={`flex items-center px-3 py-1 border rounded-full text-[10px] font-semibold uppercase tracking-wide ${b.cls}`}>
                    {b.icon}{b.label}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-white font-semibold text-xs uppercase tracking-wider mb-5">Plattform</h4>
              <ul className="space-y-3 text-sm text-slate-500">
                <li><Link to="/produkt/orbis" className="hover:text-white transition-colors">Orbis</Link></li>
                <li><Link to="/produkt/litigation-engine" className="hover:text-white transition-colors">{FEATURE_LITIGATION}</Link></li>
                <li><button onClick={openAudit} className="hover:text-white transition-colors">Analyse anfragen</button></li>
                <li><button onClick={() => setIsPartnerModalOpen(true)} className="hover:text-white transition-colors">Partner Programm</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold text-xs uppercase tracking-wider mb-5">Unternehmen</h4>
              <ul className="space-y-3 text-sm text-slate-500">
                <li className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-slate-700" /> Michelhausen, AT</li>
                <li><a href="mailto:office@arkorbit.at" className="flex items-center gap-2 hover:text-white transition-colors"><Mail className="w-3.5 h-3.5 text-slate-700" /> office@arkorbit.at</a></li>
                <li>
                  <a href="https://www.linkedin.com/in/andreas-berger-b7b5943b5" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:text-white transition-colors">
                    <Linkedin className="w-3.5 h-3.5 text-slate-700" /> LinkedIn
                  </a>
                </li>
                <li><button onClick={onLoginClick} className="flex items-center gap-2 hover:text-white transition-colors"><Lock className="w-3.5 h-3.5 text-slate-700" /> Kunden-Login</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-slate-600">
            <p>© {new Date().getFullYear()} {LEGAL_ENTITY.NAME} · UID {LEGAL_ENTITY.UID}</p>
            <div className="flex items-center gap-5">
              <button onClick={() => setIsImpressumOpen(true)} className="hover:text-slate-400 transition-colors underline-offset-2 hover:underline">Impressum</button>
              <button onClick={() => setIsPrivacyOpen(true)} className="hover:text-slate-400 transition-colors underline-offset-2 hover:underline">Datenschutz</button>
              <button onClick={() => setIsAGBOpen(true)} className="hover:text-slate-400 transition-colors underline-offset-2 hover:underline">AGB & AVV</button>
            </div>
          </div>
        </div>
      </footer>

      {/* ─────────────────────────────────────────────────────────────────────
          MODALS – alle unverändert, nur visuell angepasst
      ───────────────────────────────────────────────────────────────────── */}

      {/* LEAD MODAL */}
      {isLeadModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsLeadModalOpen(false)} />
          <div className="relative z-10 w-full sm:max-w-md bg-[#0d1117] border border-white/8 rounded-t-3xl sm:rounded-2xl p-8 shadow-2xl">
            <button onClick={() => setIsLeadModalOpen(false)} className="absolute top-5 right-5 text-slate-600 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
            {formStep === 'form' ? (
              <>
                <div className="mb-7">
                  <h3 className="text-xl font-bold text-white mb-2">Kostenlose Analyse anfragen</h3>
                  <p className="text-slate-400 text-sm">30 Minuten · Unverbindlich · Individuelles Angebot für Ihre Kanzlei.</p>
                  <div className="mt-4 space-y-2">
                    {['Konkrete Zeitersparnis berechnen', 'Kompatibilität mit BMD/RZL prüfen', 'Maßgeschneidertes Angebot'].map(t => (
                      <div key={t} className="flex items-center gap-2 text-sm text-slate-400">
                        <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />{t}
                      </div>
                    ))}
                  </div>
                </div>
                <form onSubmit={handleSubmit} className="space-y-3">
                  {[
                    { placeholder: 'Ihr Name', key: 'name', type: 'text' },
                    { placeholder: 'Kanzlei E-Mail', key: 'email', type: 'email' },
                    { placeholder: 'Name der Kanzlei', key: 'company', type: 'text' },
                  ].map(f => (
                    <input key={f.key} type={f.type} placeholder={f.placeholder} required
                      className="w-full bg-[#060a0f] border border-white/8 rounded-xl px-4 py-3 text-white placeholder-slate-600 focus:border-blue-500/50 outline-none text-sm transition-colors"
                      value={(formData as any)[f.key]} onChange={e => setFormData({ ...formData, [f.key]: e.target.value })} />
                  ))}
                  <select className="w-full bg-[#060a0f] border border-white/8 rounded-xl px-4 py-3 text-slate-400 focus:border-blue-500/50 outline-none text-sm transition-colors appearance-none"
                    value={formData.interest} onChange={e => setFormData({ ...formData, interest: e.target.value })}>
                    <option>Analyse anfragen</option>
                    <option>Orbis – Demo</option>
                    <option>{FEATURE_LITIGATION} – Anfrage</option>
                    <option>Partner Programm</option>
                  </select>
                  <input type="hidden" name="quelle" value={formData.quelle} />
                  <button disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3.5 rounded-xl text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60 mt-2">
                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Analyse anfragen</>}
                  </button>
                  {formData.quelle && <p className="text-center text-slate-700 text-[10px] mt-1">Quelle: {formData.quelle}</p>}
                  <p className="text-center text-slate-600 text-xs">100 % diskret · Datenschutzerklärung gilt</p>
                </form>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-emerald-500/15 border border-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
                  <Check className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Anfrage eingegangen.</h3>
                <p className="text-slate-400 text-sm">Danke, {formData.name}. Wir melden uns innerhalb von 24h persönlich bei Ihnen.</p>
                <button onClick={() => setIsLeadModalOpen(false)} className="mt-6 text-blue-400 text-sm font-medium hover:text-white transition-colors">Schließen</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PARTNER MODAL */}
      {isPartnerModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsPartnerModalOpen(false)} />
          <div className="relative z-10 w-full max-w-md bg-[#0d1117] border border-white/8 rounded-2xl p-8 shadow-2xl">
            <button onClick={() => setIsPartnerModalOpen(false)} className="absolute top-5 right-5 text-slate-600 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
            <div className="mb-6">
              <h3 className="text-xl font-bold text-white mb-2">Partner Programm</h3>
              <p className="text-slate-400 text-sm">20 % auf Setup + 20 % monatlich. Für jeden vermittelten Kunden, solange er Kunde ist.</p>
            </div>
            <form onSubmit={handlePartnerSubmit} className="space-y-3">
              <input type="text" placeholder="Ihr Name" required className="w-full bg-[#060a0f] border border-white/8 rounded-xl px-4 py-3 text-white placeholder-slate-600 focus:border-blue-500/50 outline-none text-sm transition-colors"
                value={partnerData.name} onChange={e => setPartnerData({ ...partnerData, name: e.target.value })} />
              <input type="email" placeholder="Ihre E-Mail" required className="w-full bg-[#060a0f] border border-white/8 rounded-xl px-4 py-3 text-white placeholder-slate-600 focus:border-blue-500/50 outline-none text-sm transition-colors"
                value={partnerData.email} onChange={e => setPartnerData({ ...partnerData, email: e.target.value })} />
              <button disabled={isSubmitting} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3.5 rounded-xl text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60">
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Als Partner bewerben'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* DATENSCHUTZ MODAL */}
      {isPrivacyOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsPrivacyOpen(false)} />
          <div className="relative z-10 w-full max-w-4xl bg-[#0d1117] border border-white/8 rounded-2xl p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
            <button onClick={() => setIsPrivacyOpen(false)} className="absolute top-5 right-5 text-slate-600 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
            <div className="flex items-center mb-8 border-b border-white/6 pb-6">
              <ShieldCheck className="w-7 h-7 text-blue-400 mr-4" />
              <div><h3 className="text-xl font-bold text-white">Datenschutzerklärung</h3><p className="text-sm text-slate-500">Gemäß DSGVO</p></div>
            </div>
            <div className="space-y-8 text-sm text-slate-400">
              <section><h4 className="font-semibold text-white mb-2">1. Verantwortlicher</h4>
                <div className="bg-white/3 border border-white/6 rounded-xl p-4 text-slate-400">
                  <strong className="text-white">{LEGAL_ENTITY.NAME}</strong><br />{LEGAL_ENTITY.ADDRESS}<br />{LEGAL_ENTITY.LOCATION}, Österreich<br />office@arkorbit.at
                </div>
              </section>
              <section><h4 className="font-semibold text-white mb-2">2. Verarbeitete Datenarten</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Bestandsdaten: Name, Unternehmensname, Anschrift</li>
                  <li>Kontaktdaten: E-Mail, Telefon</li>
                  <li>Zahlungsdaten: Stripe Payments Europe Ltd. (Irland/EU)</li>
                  <li>Inhaltsdaten: Audiodiktate, Chat-Verläufe (Self-Hosted, kein Drittanbieter)</li>
                </ul>
              </section>
              <section><h4 className="font-semibold text-white mb-2 flex items-center gap-2"><Globe className="w-4 h-4 text-blue-400" /> 3. Dienstleister & Sub-Auftragsverarbeiter</h4>
                <p className="mb-3">Unser System ist vollständig <strong className="text-emerald-400">Self-Hosted</strong> auf Hetzner Frankfurt. Keine US-KI-Anbieter.</p>
                <div className="grid md:grid-cols-2 gap-3">
                  {[
                    { name: 'Hetzner Online GmbH', info: 'Frankfurt · ISO 27001 · AVV vorhanden', cls: 'bg-white/3 border-white/6' },
                    { name: 'KI-Engine (Self-Hosted)', info: 'Ollama + Mistral + Whisper · Keine Datenübermittlung in die USA', cls: 'bg-emerald-500/4 border-emerald-500/10' },
                    { name: 'Stripe Payments Europe Ltd.', info: 'Irland/EU · Nur Zahlungsabwicklung', cls: 'bg-white/3 border-white/6' },
                    { name: 'Matrix (Self-Hosted)', info: 'matrix.arkorbit.at · E2EE · Keine Metadaten an Dritte', cls: 'bg-emerald-500/4 border-emerald-500/10' },
                  ].map((d, i) => (
                    <div key={i} className={`border rounded-xl p-4 ${d.cls}`}>
                      <strong className="text-white text-sm">{d.name}</strong>
                      <p className="text-xs mt-1 text-slate-500">{d.info}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-slate-500">
                  <strong className="text-amber-400/80">{FEATURE_LITIGATION}</strong> – Bei Nutzung von Claude Opus (Anthropic): <strong>Doppelte Anonymisierung</strong> – Anwalt anonymisiert selbst, ARKORBIT pseudonymisiert vor Versand ein weiteres Mal. Kein Kanzleibezug für Anthropic erkennbar.
                </p>
              </section>
              <div className="grid md:grid-cols-2 gap-6">
                <section><h4 className="font-semibold text-white mb-2">4. Speicherdauer</h4><p>Nur so lange wie vertraglich nötig (7 Jahre Rechnungen gemäß BAO).</p></section>
                <section><h4 className="font-semibold text-white mb-2">5. Ihre Rechte</h4><p>Auskunft, Berichtigung, Löschung jederzeit unter office@arkorbit.at. Beschwerderecht bei der österr. Datenschutzbehörde.</p></section>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AGB MODAL */}
      {isAGBOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsAGBOpen(false)} />
          <div className="relative z-10 w-full max-w-4xl bg-[#0d1117] border border-white/8 rounded-2xl p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
            <button onClick={() => setIsAGBOpen(false)} className="absolute top-5 right-5 text-slate-600 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
            <div className="flex items-center mb-8 border-b border-white/6 pb-6">
              <Gavel className="w-7 h-7 text-blue-400 mr-4" />
              <div><h3 className="text-xl font-bold text-white">Allgemeine Geschäftsbedingungen</h3><p className="text-sm text-slate-500">Stand: {T05_LEGAL_STAND}</p></div>
            </div>
            <div className="space-y-6 text-sm text-slate-400">
              <div className="grid md:grid-cols-2 gap-6">
                <section><h4 className="font-semibold text-white mb-2">§ 1 Geltungsbereich</h4><p>Diese AGB gelten für alle SaaS-Leistungen von {LEGAL_ENTITY.NAME}. Ausschließlich B2B. Verbraucher sind ausgeschlossen.</p></section>
                <section><h4 className="font-semibold text-white mb-2">§ 2 Vertragsgegenstand</h4><p>Die Kanzlei-Plattform <strong className="text-white">{PRODUCT_NAME}</strong> (Self-Hosted). KI-gestützte IT-Infrastruktur. Keine Rechtsberatung. Kein sachlicher oder juristischer Erfolg geschuldet.</p></section>
                <section><h4 className="font-semibold text-white mb-2">§ 3 Zahlung & Vertrag</h4><p>{T05_LEGAL_PAYMENT_PARAGRAPH}</p></section>
                <section><h4 className="font-semibold text-white mb-2">§ 4 Mitwirkungspflichten</h4><p>Zugangsdaten vertraulich behandeln. Qualität der Eingabedaten liegt beim Kunden.</p></section>
              </div>
              <section className="bg-red-500/5 border border-red-500/10 rounded-xl p-5">
                <h4 className="font-semibold text-red-400 mb-2 flex items-center gap-2"><FileWarning className="w-4 h-4" /> § 5 Haftung & Prüfpflicht</h4>
                <p>KI-Ergebnisse sind maschinell generierte Vorschläge. <strong className="text-white">KI-generierte Inhalte ersetzen keine anwaltliche Beratung. Die Prüfpflicht und Verantwortung für alle KI-Outputs liegen ausschließlich beim Nutzer / Rechtsanwalt.</strong> Haftung nur für Vorsatz und grobe Fahrlässigkeit.</p>
              </section>
              <div className="grid md:grid-cols-2 gap-6">
                <section><h4 className="font-semibold text-white mb-2">§ 6 SLA</h4><p>98 % Erreichbarkeit im Jahresmittel angestrebt.</p></section>
                <section><h4 className="font-semibold text-white mb-2">§ 7 Schlussbestimmungen</h4><p>Österreichisches Recht. Gerichtsstand: Österreich.</p></section>
              </div>
              <section className="bg-white/3 border border-white/6 rounded-xl p-5">
                <h4 className="font-semibold text-white mb-2">§ 8 Datenhoheit & Export</h4>
                <p>Garantierter Export aller Kundendaten in Standardformaten bei Beendigung; optional Übernahme des Serververtrags. Kein Vendor Lock-in.</p>
              </section>

              <div className="mt-10 pt-8 border-t border-white/6">
                <div className="flex items-center mb-6">
                  <FileText className="w-6 h-6 text-violet-400 mr-3" />
                  <div><h3 className="text-lg font-bold text-white">Anhang 1: Auftragsverarbeitungsvertrag (AVV)</h3><p className="text-xs text-slate-500">Gemäß Art. 28 DSGVO</p></div>
                </div>
                <div className="space-y-5 text-sm text-slate-400">
                  <section><h4 className="font-semibold text-white mb-2">1. Gegenstand</h4><p>Verarbeitung personenbezogener Daten im Auftrag der Kanzlei. Dauer = Laufzeit des Hauptvertrages.</p></section>
                  <div className="grid md:grid-cols-2 gap-5">
                    <section><h4 className="font-semibold text-white mb-2">2. Art der Daten</h4><ul className="list-disc pl-4 space-y-1 text-xs"><li>Mandantendaten</li><li>Falldaten & Kommunikation</li><li>Audiodaten</li><li>Mitarbeiter & Dritte</li></ul></section>
                    <section><h4 className="font-semibold text-white mb-2">3. TOMs (Art. 32 DSGVO)</h4><p className="text-xs">Verschlüsselung, Zugangskontrollen, Zero-Data-Retention bei KI-APIs.</p></section>
                  </div>
                  <section className="bg-white/3 border border-white/6 rounded-xl p-4">
                    <h4 className="font-semibold text-white mb-3">4. Sub-Auftragsverarbeiter</h4>
                    <ul className="grid md:grid-cols-2 gap-2 text-xs text-slate-500">
                      <li>• Hetzner Online GmbH (Frankfurt)</li>
                      <li>• Matrix Homeserver (self-hosted)</li>
                      <li className="text-emerald-400/80">✓ Keine US-KI-Anbieter</li>
                    </ul>
                    <p className="text-xs text-slate-500 mt-3">
                      Zahlungsabwicklung über Stripe Payments Europe Ltd. nur für Zahlungsdaten des Auftraggebers; keine
                      Mandantendaten. Details in der Datenschutzerklärung.
                    </p>
                  </section>
                  <section className="bg-violet-500/5 border border-violet-500/10 rounded-xl p-4 text-center">
                    <h4 className="font-semibold text-violet-400 mb-2">Rechtliche Bindung (Clickwrap)</h4>
                    <p className="text-xs text-slate-500">Durch ausdrückliche Zustimmung im Vertragsabschluss rechtlich bindend. Physische Unterschrift nicht erforderlich.</p>
                  </section>
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-6">{T05_LEGAL_TRADEMARK_FOOTNOTE}</p>
            </div>
          </div>
        </div>
      )}

      {/* IMPRESSUM MODAL */}
      {isImpressumOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsImpressumOpen(false)} />
          <div className="relative z-10 w-full max-w-3xl bg-[#0d1117] border border-white/8 rounded-2xl p-8 shadow-2xl max-h-[90vh] overflow-y-auto">
            <button onClick={() => setIsImpressumOpen(false)} className="absolute top-5 right-5 text-slate-600 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
            <div className="flex items-center mb-8 border-b border-white/6 pb-6">
              <Scale className="w-7 h-7 text-blue-400 mr-4" />
              <div><h3 className="text-xl font-bold text-white">Impressum & Offenlegung</h3><p className="text-sm text-slate-500">Gemäß § 25 Mediengesetz & § 5 ECG</p></div>
            </div>
            <div className="grid md:grid-cols-2 gap-8 text-sm text-slate-400">
              <div>
                <div className="bg-white/3 border border-white/6 rounded-xl p-5 mb-5">
                  <h4 className="font-semibold text-white text-xs uppercase tracking-wider mb-3 border-b border-white/6 pb-2">Medieninhaber</h4>
                  <p className="font-bold text-white mb-3">{LEGAL_ENTITY.NAME}</p>
                  <p>{LEGAL_ENTITY.ADDRESS}<br />{LEGAL_ENTITY.LOCATION}<br />Österreich</p>
                  <div className="mt-3 pt-3 border-t border-white/6 text-xs space-y-1">
                    <p><span className="text-slate-600">E-Mail:</span> office@arkorbit.at</p>
                    <p><span className="text-slate-600">Web:</span> www.arkorbit.at</p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">Dienstleistungen in der automatischen Datenverarbeitung und Informationstechnik. Entwicklung und Vertrieb von Software für juristische Berufe.</p>
              </div>
              <div>
                <div className="bg-white/3 border border-white/6 rounded-xl p-5 mb-5">
                  <h4 className="font-semibold text-white text-xs uppercase tracking-wider mb-3 border-b border-white/6 pb-2">Gewerbe & Aufsicht</h4>
                  <ul className="space-y-1.5 text-xs">
                    <li><strong className="text-slate-300">Kammer:</strong> WKO Niederösterreich · UBIT</li>
                    <li><strong className="text-slate-300">Aufsicht:</strong> BH Tulln</li>
                    <li><strong className="text-slate-300">UID:</strong> ATU83006408</li>
                  </ul>
                </div>
                <p className="text-xs text-slate-600">ODR: <a href="http://ec.europa.eu/odr" target="_blank" className="text-blue-400/70 hover:text-blue-400 transition-colors">ec.europa.eu/odr</a></p>
              </div>
              <div className="md:col-span-2 border-t border-white/6 pt-6 text-xs text-slate-600 leading-relaxed grid md:grid-cols-2 gap-6">
                <p><strong className="text-slate-400">Haftung für Inhalte:</strong> Inhalte mit größter Sorgfalt erstellt. Angebot richtet sich ausschließlich an Unternehmer (B2B).</p>
                <p><strong className="text-slate-400">Urheberrecht:</strong> Inhalte unterliegen österr. Urheberrecht. Vervielfältigung nur mit schriftlicher Zustimmung.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <ChatWidget />
    </div>
  );
};

export default LandingPage;
